/* /mental-acuity scoring — a PURE function of logged raw metrics.
 *
 *   aggregate(state, ctx) → raw      the trial arrays reduced to the numbers the
 *                                    formulas read; the page logs EXACTLY this
 *                                    object with tool_complete
 *   score(raw, norms)     → result   domains, composite, percentiles, level,
 *                                    flags — nothing else is an input
 *
 * Why the split (25.09.2026). The age norms are re-cut from real runs
 * (config/mental-acuity-norms.php, scripts/mental-acuity-norms.mjs), and a norm
 * table is only valid for the formula that produced the scores it was cut from.
 * While scoring lived inline and the log carried only some of its inputs, every
 * formula change voided the history: the fix for a saturated term could not be
 * re-normed without weeks of new runs. With every input logged and the formula
 * in one module that Node can load, a formula change is re-normed the same day
 * by re-scoring the logged runs.
 *
 * Scoring version 3 (sv:3). Four terms were anchored on published numbers that
 * our people do not produce, and sat on their clamps for most runs (561 real
 * runs, 21.08–24.09.2026):
 *   Stroop speed         78 % exactly 0   (anchor 350–1100 ms; real median 1365)
 *   Stroop interference  72 % exactly 100 (anchor +90 ms; real median −175 ms —
 *                        block C comes after two blocks of practice)
 *   Flanker interference 53 % exactly 0   (anchor +70 ms; real median +162 ms)
 *   switch cost (TMT B/A) 29 % exactly 0  (anchor B/A 2.1; real median 2.54)
 * A term pinned to a clamp measures nothing for the people on the clamp. They
 * are now centred on the real median with the real spread (log scale for a
 * time, logistic for a difference or a ratio): the median person scores 50,
 * the quartiles 25/75, and nothing piles up on an end. The percentile a person
 * SEES comes from the empirical norms either way — this is about resolution.
 * ⚠ The anchors were measured on the OLD flanker stimulus (flankers drawn
 *   larger than the target on 60 % of incongruent trials, removed in the same
 *   change as flk_v:2). Re-anchor the flanker term on flk_v:2 runs — see the
 *   2026-10-16 watch.
 *
 * Also fixed in sv:3: a Flanker run with no correct incongruent (or congruent)
 * response had an EMPTY reaction-time list, its mean read as 0, the
 * «interference» came out as −(congruent RT) and scored the MAXIMUM. 79 runs
 * (14 %) that failed the task outright got the best interference score of
 * anyone. With nothing to compare, interference is now scored 0.
 *
 * Scoring version 4 (sv:4, 21.10.2026), measured on the 519 sv:3 runs
 * (30.09–20.10). sv:3 rows carry every input v4 reads, so they are re-scored,
 * not dropped (scripts/mental-acuity-rescore.mjs).
 *   · Domains follow what each one says it measures. The trick questions (CRT)
 *     were half of «working memory» — but the question stays on the screen, and
 *     what they ask is to hold back the first answer: that is inhibitory control,
 *     where they now sit with Flanker and Stroop (CRT correlates with Flanker .27,
 *     with sequence memory .20). Stroop interference was ALSO half of «cognitive
 *     flexibility», whose text names only Trail Making B/A — Stroop is inhibition
 *     (Miyake et al. 2000), B/A is shifting. Domain weights move with their tasks
 *     so each task keeps about its share of the composite: sequence 0.10 → 0.10,
 *     CRT 0.10 → 0.083, Flanker and Stroop 0.075 → 0.083.
 *   · Stroop reads medians of CORRECT responses and the classic C − A. The old
 *     C − mean(A,B) had a real median of −192 ms — block B (name the WORD inside
 *     a coloured shape) is itself an interference block (median 1182 ms against
 *     802 ms for A), so «interference» came out negative for 72 % of people.
 *     C − A: median +162 ms, negative for 14 % — the textbook direction.
 *   · Flanker speed was anchored at 650 ms; the real mean RT is 730 ms, and 78 %
 *     of runs scored exactly 0. Flanker interference re-anchored on flk_v:2 runs
 *     (the stimulus changed on 25.09): median 118 ms, was 162.
 *   · CRT is scored as CRT is: the share of right answers. It added up to +10
 *     for answering FAST — in the one task that asks you not to — and −5 per
 *     intuitive answer on top of that answer already being wrong.
 *   · Matrices: 24 % of runs were exactly 100 — every perfect run, however slow;
 *     the time bonus was clamped away. Accuracy now takes 95 and the bonus the
 *     last 5, so perfect runs are told apart by time.
 * Check against what is known: speed and inhibition decline with age. On the
 * same 383 adult runs the trend per decade went processing speed −4.0 → −6.2,
 * inhibitory control −3.8 → −5.1, composite −2.9 → −3.9 points: the v4 terms
 * see age more clearly, not less. Clamps: Flanker speed at 0 78 % → 2 %,
 * matrices at 100 24 % → 0 %.
 *
 * Measured and deliberately NOT changed (22.10.2026, same runs):
 *   · Stroop block B (name the WORD inside a coloured shape) is logged, not
 *     scored: B − A relates neither to age (r .07; C − A .18) nor to Flanker
 *     interference (−.02). It stays — removing it would save ~2 % of the test
 *     and change what block C follows, i.e. the interference anchor.
 *   · Matrices: Cronbach α .53; items 1–7 are passed by 95–99 %, item 9 by 35 %
 *     with the weakest item-rest r (.13; its size rule is a small-vs-large
 *     checkerboard); the 1–4 weights add nothing (r with the other five domains
 *     .251 weighted, .261 unweighted). Every item has exactly one answer that
 *     fits a rule for every attribute (tests/js/mental-acuity-matrices.test.mjs).
 *     The bank is rebuilt from `mat_picks` — which wrong option each item draws —
 *     not by guess.
 */
(function (root) {
    'use strict';

    var VERSION = 4;
    /* The oldest logged rows the current formula can read: every v4 input is in sv:3. */
    var RESCORABLE_FROM = 3;

    /* TMT circles. Part B was 16 (1–8, A–H) until 20.10.2026 and is 15 (1–8, A–G)
       since — the 8th Russian letter «З» is the digit 3 in our font (comment
       #1950). Rows logged before carry no tr_b_n and mean 16. */
    var TRAIL_A_CIRCLES = 15, TRAIL_B_CIRCLES_OLD = 16;

    /* Centres (median) and logistic scales (IQR / 2 ln 3) of the real runs:
       519 sv:3 runs, 30.09–20.10.2026, except the switch cost (561 runs,
       21.08–24.09; re-measured on sv:3: 0.844 / 0.232 — same within noise). */
    var ANCHORS = {
        stroopSpeedMed: 802, stroopSpeedS: 104,  /* ms: block A (colour squares), median of correct */
        stroopIntMed: 162, stroopIntS: 130,      /* ms: C − A, medians of correct */
        flankerSpeedMed: 730, flankerSpeedS: 74, /* ms: mean RT of correct responses, both conditions */
        flankerEffMed: 118, flankerEffS: 44,     /* ms: correct incongruent − correct congruent (flk_v:2) */
        /* ln(TMT B / TMT A) per MOVE. Measured as 0.9305 on the old board (15 moves
           in B, 14 in A); the per-move centre is that minus ln(15/14), so a
           15-circle B is read at the same pace as a 16-circle one. */
        switchLnMed: 0.9305 - Math.log((TRAIL_B_CIRCLES_OLD - 1) / (TRAIL_A_CIRCLES - 1)), switchLnS: 0.2218
    };

    /* Tombaugh (2004) TMT seconds, 25 circles per part; ours has 15 (A) and 15 (B, 16 before 20.10). */
    var TRAIL_PUBLISHED = {
        '18-29': {a: 23.4, b: 49.6}, '30-39': {a: 26.0, b: 54.0},
        '40-49': {a: 30.0, b: 61.0}, '50-59': {a: 31.8, b: 71.0},
        '60-69': {a: 36.0, b: 83.0}, '70+':   {a: 46.5, b: 100.7}
    };
    var TRAIL_A_SCALE = TRAIL_A_CIRCLES / 25;
    /* Tombaugh, Kozak & Rees (1999) — animals named in 60 s, mean by age. */
    var FLUENCY_PUBLISHED = {
        '18-29': 22.0, '30-39': 21.7, '40-49': 21.0,
        '50-59': 20.0, '60-69': 19.0, '70+': 17.0
    };
    var MAT_WEIGHTS = [1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 4, 4];
    var WEIGHTS = {workingMemory: 0.10, processingSpeed: 0.18, cognitiveFlexibility: 0.18,
                   inhibitoryControl: 0.25, abstractReasoning: 0.17, verbalFluency: 0.12};
    var DOMAINS = ['workingMemory', 'processingSpeed', 'cognitiveFlexibility', 'inhibitoryControl', 'abstractReasoning', 'verbalFluency'];

    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
    function mean(a) { if (!a || !a.length) return 0; var s = 0; for (var i = 0; i < a.length; i++) s += a[i]; return s / a.length; }
    function median(a) {
        if (!a || !a.length) return null;
        var b = a.slice().sort(function (x, y) { return x - y; }), m = b.length >> 1;
        return b.length % 2 ? b[m] : (b[m - 1] + b[m]) / 2;
    }
    function count(a) { var n = 0; for (var i = 0; i < (a || []).length; i++) if (a[i]) n++; return n; }
    function pct(ok, n) { return n ? ok / n * 100 : 0; }
    function logistic(x, med, s) { return 100 / (1 + Math.exp((x - med) / s)); }
    function r0(v) { return v === null || v === undefined || !isFinite(v) ? null : Math.round(v); }

    /* ── bands ─────────────────────────────────────────────────────────── */
    function isMinor(age, norms) { return age < ((norms && norms.adultFrom) || 18); }
    function normBand(age, norms) {
        if (isMinor(age, norms)) return (norms && norms.minorNormBand) || '18-29';
        var bands = (norms && norms.bands) || [];
        for (var i = 0; i < bands.length; i++) if (bands[i].to === null || age <= bands[i].to) return bands[i].key;
        return bands.length ? bands[bands.length - 1].key : '18-29';
    }
    function personBand(age, norms) {
        if (!isMinor(age, norms)) return normBand(age, norms);
        var mb = (norms && norms.minorBands) || [];
        for (var i = 0; i < mb.length; i++) if (age >= mb[i].from && age <= mb[i].to) return mb[i].key;
        return 'minor';
    }
    /* Piecewise-linear through p10..p90. A zero-width segment can only be entered
       at its own knot, so the knot's percentile is the answer. */
    function percentile(score, knots) {
        if (!knots) return 50;
        var p10 = knots[0], p25 = knots[1], p50 = knots[2], p75 = knots[3], p90 = knots[4];
        function seg(x, a, b, pa, pb) { return b > a ? pa + (x - a) / (b - a) * (pb - pa) : pb; }
        if (score <= p10) return p10 > 0 ? 5 + (score / p10) * 5 : 5;
        if (score <= p25) return seg(score, p10, p25, 10, 25);
        if (score <= p50) return seg(score, p25, p50, 25, 50);
        if (score <= p75) return seg(score, p50, p75, 50, 75);
        if (score <= p90) return seg(score, p75, p90, 75, 90);
        return p90 < 100 ? 90 + ((score - p90) / (100 - p90)) * 10 : 95;
    }

    /* ── state → raw ───────────────────────────────────────────────────── */
    /* ctx: {matricesKeys:[correct index per item], trickCount, trickIds:[id per
             question index], flankerVersion, tricksVersion, sequenceVersion,
             flankerTrials, stroopTrials, practiceSkipped} */
    function aggregate(s, ctx) {
        ctx = ctx || {};
        var st = s.stroop, fk = s.flanker, tr = s.trail, mx = s.matrices, tk = s.tricks, sq = s.sequence;
        function correctRts(rts, acc) { var o = []; for (var i = 0; i < rts.length; i++) if (acc[i]) o.push(rts[i]); return o; }
        /* Per question: c right · t the intuitive (trap) answer · w another wrong
           one · - nothing. An item set that changes (crt_v) can then be re-normed
           on the items the old and new sets share. */
        var crtItems = {};
        if (ctx.trickIds) {
            for (var k = 0; k < tk.answers.length; k++) {
                var a = tk.answers[k] || {}, id = ctx.trickIds[a.qi];
                if (id === undefined) continue;
                crtItems[id] = tk.correct[k] ? 'c' : a.trap ? 't' : (typeof a.answer === 'number' && isFinite(a.answer)) ? 'w' : '-';
            }
        }
        var keys = ctx.matricesKeys || [];
        var matW = 0, matWmax = 0, items = '', picks = '';
        for (var i = 0; i < keys.length; i++) {
            var w = MAT_WEIGHTS[i] || 1, ans = mx.answers[i];
            matWmax += w;
            if (ans === keys[i]) { matW += w; items += '1'; }
            else items += (ans === undefined || ans === -1) ? '-' : '0';
            /* the option picked (0–3), '-' none: which wrong option draws people */
            picks += (typeof ans === 'number' && ans >= 0 && ans < 10) ? String(ans) : '-';
        }
        var fkOkC = count(fk.congruent_acc), fkOkI = count(fk.incongruent_acc);
        var aMn = mean(st.a_rt), bMn = mean(st.b_rt), cMn = mean(st.c_rt);
        var base = (aMn + bMn) / 2;
        return {
            sv: VERSION,
            flk_v: ctx.flankerVersion || 2,
            /* Task versions: a change of what the person sees, which the norms can
               only absorb by re-cutting on rows of the new version.
               crt_v 2 (21.10.2026): the eraser item (the bat-and-ball again, same
               numbers) replaced by the 15th-from-both-ends item; seq_v 2: no tile
               twice in one sequence. */
            crt_v: ctx.tricksVersion || 1,
            seq_v: ctx.sequenceVersion || 1,
            /* mat_v 2 (22.10.2026): options in a fresh order — the answer sat first in 6 of 12 items. */
            mat_v: ctx.matricesVersion || 1,
            /* Earlier runs on this device (the page's local history); null — the
               history could not be read. A retake scores 3.5 composite points above
               the first attempt on average (55 addresses with two runs, 21.10.2026),
               so the norms are cut from rt 0 and a retaker is told. */
            rt: typeof ctx.previousRuns === 'number' ? ctx.previousRuns : null,
            /* No age here: this object IS the telemetry payload, and a band is
               what the norms need — score() adds the bands. */
            edu: s.profile.education,
            sleep: s.profile.sleep,
            /* Stroop: per block, median of CORRECT responses (what the formula
               reads since sv:4) and mean of ALL responses (what sv:3 read). */
            st_a_mn: r0(aMn), st_b_mn: r0(bMn), st_c_mn: r0(cMn),
            st_a_md: r0(median(correctRts(st.a_rt, st.a_acc))),
            st_b_md: r0(median(correctRts(st.b_rt, st.b_acc))),
            st_c_md: r0(median(correctRts(st.c_rt, st.c_acc))),
            st_a_acc: r0(pct(count(st.a_acc), st.a_acc.length)),
            st_b_acc: r0(pct(count(st.b_acc), st.b_acc.length)),
            st_c_acc: r0(pct(count(st.c_acc), st.c_acc.length)),
            st_n: st.a_rt.length + st.b_rt.length + st.c_rt.length,
            /* v2 names, kept so the older rows and the newer ones read alike */
            stroop_base_ms: r0(base),
            stroop_effect_ms: r0(cMn - base),
            stroop_acc: r0(pct(count(st.c_acc), st.c_acc.length)),
            crt_correct: count(tk.correct),
            crt_n: tk.correct.length,
            crt_ms: r0(mean(tk.times)),
            crt_traps: tk.answers.filter(function (a) { return a && a.trap; }).length,
            crt_items: crtItems,
            trail_a_ms: r0(tr.a_time), trail_b_ms: r0(tr.b_time),
            tr_b_n: tr.b_n || TRAIL_B_CIRCLES_OLD,
            tr_a_err: tr.a_errors, tr_b_err: tr.b_errors,
            trail_errs: tr.a_errors + tr.b_errors,
            matrices_correct: matW, mat_wmax: matWmax, matrices_items: items, mat_picks: picks,
            mat_n: mx.answers.length, mat_ms: r0(mean(mx.times)), mat_to: mx.timeouts,
            fk_n_c: fk.congruent_acc.length, fk_n_i: fk.incongruent_acc.length,
            fk_ok_c: fkOkC, fk_ok_i: fkOkI,
            fk_rt_c: fkOkC ? r0(mean(fk.congruent_rt)) : null,
            fk_rt_i: fkOkI ? r0(mean(fk.incongruent_rt)) : null,
            fk_rt_all: r0(mean(fk.congruent_rt.concat(fk.incongruent_rt))),
            fk_miss: fk.misses || 0,
            flanker_effect_ms: r0((mean(fk.incongruent_rt) - mean(fk.congruent_rt)) || 0),
            flanker_acc: r0((pct(fkOkC, fk.congruent_acc.length) + pct(fkOkI, fk.incongruent_acc.length)) / 2),
            fluency_words: s.fluency.words.length, fluency_dups: s.fluency.duplicates,
            span: sq.maxSpan, seq_first: sq.firstTrySuccesses, seq_rounds: sq.totalRounds,
            prac_skip: ctx.practiceSkipped || 0,
            n_crt: ctx.trickCount || 0, n_mat: keys.length, n_fk: ctx.flankerTrials || 0, n_st: ctx.stroopTrials || 0
        };
    }

    /* ln of the B/A time per move (a part with n circles has n − 1 timed moves:
       the clock starts on the first tap). */
    function switchLn(raw) {
        var movesA = TRAIL_A_CIRCLES - 1, movesB = (raw.tr_b_n || TRAIL_B_CIRCLES_OLD) - 1;
        return Math.log((raw.trail_b_ms / movesB) / (raw.trail_a_ms / movesA));
    }

    /* ── the terms (exported so the norm script re-scores logged runs) ─── */
    function terms(raw) {
        var t = {};
        /* Stroop: medians of correct responses; no correct answer in a block =
           nothing to measure there (scored 0, as an empty Flanker condition). */
        var aMd = raw.st_a_md || 0, cMd = raw.st_c_md || 0;
        t.stroopSpeed = aMd > 0 ? logistic(aMd, ANCHORS.stroopSpeedMed, ANCHORS.stroopSpeedS) : 0;
        t.stroopInt = aMd > 0 && cMd > 0 ? logistic(cMd - aMd, ANCHORS.stroopIntMed, ANCHORS.stroopIntS) : 0;
        t.stroopAcc = clamp(((raw.stroop_acc || 0) - 25) / 75 * 100, 0, 100);
        t.stroop = t.stroopSpeed * 0.30 + t.stroopInt * 0.45 + t.stroopAcc * 0.25;

        t.tricks = raw.crt_n ? raw.crt_correct / raw.crt_n * 100 : 0;

        var band = raw.norm_band || '18-29';
        var ref = TRAIL_PUBLISHED[band] || TRAIL_PUBLISHED['18-29'];
        var timeScore = function (ms, r) { return ms > 0 && r > 0 ? clamp(50 * (1 - Math.log2(ms / r)), 0, 100) : 0; };
        t.trailA = clamp(timeScore(raw.trail_a_ms, ref.a * TRAIL_A_SCALE * 1000) - (raw.tr_a_err || 0) * 5, 0, 100);
        t.switchCost = raw.trail_a_ms > 0 && raw.trail_b_ms > 0
            ? logistic(switchLn(raw), ANCHORS.switchLnMed, ANCHORS.switchLnS) : 0;

        var matAcc = raw.mat_wmax ? raw.matrices_correct / raw.mat_wmax : 0;
        var matBonus = matAcc > 0.8 ? clamp(5 - ((raw.mat_ms || 0) / 45000) * 5, 0, 5) : 0;
        t.matrices = clamp(matAcc * 95 + matBonus, 0, 100);

        t.flankerSpeed = raw.fk_rt_all > 0 ? logistic(raw.fk_rt_all, ANCHORS.flankerSpeedMed, ANCHORS.flankerSpeedS) : 0;
        /* No correct answer in one of the conditions = nothing to compare. The
           effect is flanker_effect_ms — the difference of the unrounded means,
           rounded once — not fk_rt_i − fk_rt_c (two roundings, ±1 ms = ±0.12
           points on the steep part): it is also the number the v2 rows carry,
           so their re-scoring is exact. */
        t.flankerInt = (raw.fk_ok_c > 0 && raw.fk_ok_i > 0)
            ? logistic(raw.flanker_effect_ms, ANCHORS.flankerEffMed, ANCHORS.flankerEffS) : 0;
        t.flankerAcc = clamp(((raw.flanker_acc || 0) - 50) / 50 * 100, 0, 100);
        t.flanker = t.flankerSpeed * 0.25 + t.flankerInt * 0.50 + t.flankerAcc * 0.25;

        var fref = FLUENCY_PUBLISHED[band] || FLUENCY_PUBLISHED['18-29'];
        t.fluency = clamp((raw.fluency_words || 0) / (fref * 2) * 100, 0, 100) - (raw.fluency_dups || 0) * 2;

        t.sequence = clamp(((raw.span || 0) - 2) / 8 * 100, 0, 100) + Math.min(10, (raw.seq_first || 0) * 2);
        return t;
    }

    function domainsOf(t) {
        return {
            workingMemory: clamp(t.sequence, 0, 100),
            processingSpeed: clamp(t.trailA * 0.5 + t.stroopSpeed * 0.5, 0, 100),
            cognitiveFlexibility: clamp(t.switchCost, 0, 100),
            inhibitoryControl: clamp((t.flanker + t.stroop + t.tricks) / 3, 0, 100),
            abstractReasoning: clamp(t.matrices, 0, 100),
            verbalFluency: clamp(t.fluency, 0, 100)
        };
    }
    function compositeOf(d) { var c = 0; for (var k in WEIGHTS) c += d[k] * WEIGHTS[k]; return clamp(c, 0, 100); }

    /* ── raw → result ──────────────────────────────────────────────────── */
    /* `age` is passed apart from `raw` on purpose: raw is logged as is, and it
       carries the age BAND (set here), never the age itself. */
    function score(raw, norms, age) {
        var band = normBand(age, norms);
        raw.age_band = personBand(age, norms);
        raw.norm_band = band;
        raw.nv = (norms && norms.version) || 0;
        var t = terms(raw), d = domainsOf(t), composite = compositeOf(d);
        var rows = (norms && norms.domains) || {};
        var percentiles = {};
        for (var i = 0; i < DOMAINS.length; i++) {
            var k = DOMAINS[i];
            percentiles[k] = clamp(percentile(d[k], (rows[k] || {})[band]), 1, 99);
        }
        var overallPct = Math.round(clamp(percentile(composite, (rows.composite || {})[band]), 1, 99));

        /* Confidence (1–5): how much of the battery was completed, and sleep. */
        var bN = raw.tr_b_n || TRAIL_B_CIRCLES_OLD;
        var total = (raw.n_st || 10) * 3 + (raw.n_crt || 0) + TRAIL_A_CIRCLES + bN + (raw.n_mat || 0) + (raw.n_fk || 0) + 1 + (raw.seq_rounds || 0);
        var done = (raw.st_n || 0) + (raw.crt_n || 0) + (raw.trail_a_ms > 0 ? TRAIL_A_CIRCLES : 0) + (raw.trail_b_ms > 0 ? bN : 0)
                 + (raw.mat_n || 0) + (raw.fk_n_c || 0) + (raw.fk_n_i || 0) + 1 + (raw.seq_rounds || 0);
        var sleepFactor = raw.sleep === 'good' ? 1 : raw.sleep === 'avg' ? 0.8 : 0.6;
        var confidence = Math.round(clamp(done / Math.max(1, total) * 2.5 + sleepFactor * 2.5, 1, 5));

        /* Named caveats: which part to trust less, and why. */
        var flags = [];
        var fkN = (raw.fk_n_c || 0) + (raw.fk_n_i || 0);
        var fkAcc = fkN ? ((raw.fk_ok_c || 0) + (raw.fk_ok_i || 0)) / fkN : 1;
        if (raw.sleep === 'poor') flags.push('flag_sleep_poor');
        if (fkN && fkAcc < 0.6) flags.push('flag_near_chance');
        if (((raw.fk_ok_c || 0) + (raw.fk_ok_i || 0)) >= 5 && raw.fk_rt_all < 250 && fkAcc < 0.7) flags.push('flag_fast_guessing');
        if ((raw.mat_to || 0) >= 3) flags.push('flag_timeouts');
        if (raw.fluency_words > 0 && raw.fluency_words < 8) flags.push('flag_fluency_low');
        if ((raw.prac_skip || 0) >= 2) flags.push('flag_practice_skipped');
        if (raw.rt >= 1) flags.push('flag_retake');

        /* Levels on a uniform percentile: top tenth, next fifth, middle 45 %, bottom quarter. */
        var level = overallPct >= 90 ? 'superior' : overallPct >= 70 ? 'above' : overallPct >= 25 ? 'average' : 'below';
        return {
            composite: Math.round(composite), domains: d, percentiles: percentiles, overallPct: overallPct,
            minor: isMinor(age, norms), normBand: band, confidence: confidence, flags: flags, terms: t, raw: raw,
            level: level, levelKey: 'results_level_' + level, levelDescKey: 'results_level_' + level + '_desc',
            levelClass: 'ma-level-' + level
        };
    }

    var api = {
        VERSION: VERSION, RESCORABLE_FROM: RESCORABLE_FROM, ANCHORS: ANCHORS, DOMAINS: DOMAINS, WEIGHTS: WEIGHTS,
        aggregate: aggregate, terms: terms, switchLn: switchLn, domainsOf: domainsOf, compositeOf: compositeOf,
        score: score, percentile: percentile, normBand: normBand, personBand: personBand, isMinor: isMinor
    };
    root.MentalAcuityScoring = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
